import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { listStockLevels } from "../../api";

const columnDefinitions = [
  { id: "article", label: "Artikel", numeric: false, sortable: true },
  { id: "location", label: "Ort", numeric: false, sortable: true },
  { id: "onHand", label: "Bestand", numeric: true, sortable: true },
  { id: "reserved", label: "Reserviert", numeric: true, sortable: true },
  { id: "available", label: "Verfügbar", numeric: true, sortable: true },
  { id: "updatedAt", label: "Aktualisiert", numeric: false, sortable: true },
];

function formatNumber(value) {
  const safeValue = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("de-DE").format(safeValue);
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function getFieldValue(row, field) {
  switch (field) {
    case "article":
      return (
        row.itemName ||
        row.partName ||
        row.description ||
        row.sku ||
        row.partNumber ||
        ""
      );
    case "location":
      return row.locationName || row.locationCode || "";
    case "onHand":
      return typeof row.onHand === "number" ? row.onHand : 0;
    case "reserved":
      return typeof row.reserved === "number" ? row.reserved : 0;
    case "available": {
      if (typeof row.available === "number") {
        return row.available;
      }
      const onHand = typeof row.onHand === "number" ? row.onHand : 0;
      const reserved = typeof row.reserved === "number" ? row.reserved : 0;
      return onHand - reserved;
    }
    case "updatedAt":
      return row.updatedAt || "";
    default:
      return row[field] ?? "";
  }
}

function descendingComparator(a, b, orderBy) {
  const aValue = getFieldValue(a, orderBy);
  const bValue = getFieldValue(b, orderBy);
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

function buildLocationOptions(levels) {
  const map = new Map();
  levels.forEach((level) => {
    const id = level.locationId || level.location?.id;
    if (!id) {
      return;
    }
    if (!map.has(id)) {
      map.set(id, {
        id,
        code: level.locationCode || level.location?.code || "",
        name: level.locationName || level.location?.name || "",
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    const aLabel = a.name || a.code;
    const bLabel = b.name || b.code;
    return aLabel.localeCompare(bLabel, "de-DE", { sensitivity: "base" });
  });
}

function buildArticleDisplay(row) {
  const name = row.itemName || row.partName || row.description;
  const sku = row.sku || row.partNumber;
  if (name && sku) {
    return `${name} (${sku})`;
  }
  return name || sku || "Unbekannter Artikel";
}

function buildLocationDisplay(row) {
  const name = row.locationName || row.location?.name;
  const code = row.locationCode || row.location?.code;
  if (name && code) {
    return `${name} · ${code}`;
  }
  return name || code || "—";
}

function buildAvailabilityChip(row) {
  const available = getFieldValue(row, "available");
  let color = "default";
  if (available > 0) {
    color = "success";
  }
  if (available < 0) {
    color = "error";
  }
  return (
    <Chip
      label={formatNumber(available)}
      color={color}
      size="small"
      variant={color === "success" ? "filled" : "outlined"}
    />
  );
}

export default function InventoryOverviewPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [orderBy, setOrderBy] = useState("available");
  const [order, setOrder] = useState("desc");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchText]);

  const sanitizedLocationId = locationFilter === "ALL" ? undefined : locationFilter;

  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      locationId: sanitizedLocationId,
    }),
    [debouncedSearch, sanitizedLocationId]
  );

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["inventory", "stock-levels", queryFilters],
    queryFn: () =>
      listStockLevels(token, {
        search: queryFilters.search,
        locationId: queryFilters.locationId,
      }),
    enabled: Boolean(token),
    keepPreviousData: true,
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
  });

  const levels = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    return [];
  }, [data]);

  const locationOptions = useMemo(() => buildLocationOptions(levels), [levels]);

  const visibleRows = useMemo(() => {
    const normalizedSearch = debouncedSearch.toLowerCase();
    const filtered = levels.filter((row) => {
      const matchesLocation =
        !sanitizedLocationId ||
        row.locationId === sanitizedLocationId ||
        row.location?.id === sanitizedLocationId;
      if (!matchesLocation) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const haystack = [
        row.itemName,
        row.partName,
        row.description,
        row.sku,
        row.partNumber,
        row.locationName,
        row.locationCode,
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());
      return haystack.some((value) => value.includes(normalizedSearch));
    });
    return stableSort(filtered, getComparator(order, orderBy));
  }, [levels, debouncedSearch, sanitizedLocationId, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          Lager
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField
            label="Suche"
            placeholder="Artikel oder Lagerort"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            sx={{ minWidth: { sm: 300 }, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="inventory-location-label">Ort</InputLabel>
            <Select
              labelId="inventory-location-label"
              label="Ort"
              value={locationFilter}
              onChange={(event) => setLocationFilter(event.target.value)}
            >
              <MenuItem value="ALL">Alle Standorte</MenuItem>
              {locationOptions.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name || location.code || "Unbenannter Standort"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Paper sx={{ position: "relative" }}>
          {isFetching && !isLoading && (
            <LinearProgress
              sx={{ position: "absolute", top: 0, left: 0, right: 0, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
            />
          )}
          {isLoading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ py: 6, px: 3 }}>
              <Typography color="error" variant="body1">
                {error.message || "Fehler beim Laden der Bestände."}
              </Typography>
            </Box>
          ) : visibleRows.length === 0 ? (
            <Box sx={{ py: 6, px: 3 }}>
              <Typography variant="body1">Keine Bestände gefunden.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columnDefinitions.map((column) => (
                    <TableCell key={column.id} align={column.numeric ? "right" : "left"}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow
                    key={row.id || `${row.stockItemId}-${row.locationId}`}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      const targetId = row.id || row.stockLevelId;
                      if (targetId) {
                        navigate(`/inventory/${targetId}`);
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {buildArticleDisplay(row)}
                      </Typography>
                      {row.uom && (
                        <Typography variant="caption" color="text.secondary">
                          Einheit: {row.uom}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{buildLocationDisplay(row)}</TableCell>
                    <TableCell align="right">{formatNumber(getFieldValue(row, "onHand"))}</TableCell>
                    <TableCell align="right">{formatNumber(getFieldValue(row, "reserved"))}</TableCell>
                    <TableCell align="right">{buildAvailabilityChip(row)}</TableCell>
                    <TableCell>{formatDateTime(getFieldValue(row, "updatedAt"))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
        <Typography variant="caption" color="text.secondary">
          Aktualisierung alle 10 Sekunden – zeigt Live-Bestände der Standorte.
        </Typography>
      </Stack>
    </Container>
  );
}
